import type { ModelComparisonCompareTextBlock } from '@backmarket/http-api/src/api-specs-model-comparison/models/compare-models'
import type { Picker } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'

import {
  PRODUCT_COMPARISON_CAMERA_FIELDS,
  PRODUCT_COMPARISON_DURABILITY_FIELDS,
  PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
  PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
  PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
  PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
  PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
  PRODUCT_COMPARISON_VARIANT_GROUP_IDS,
} from '../composables/useEnrichResultsPage/useEnrichResultsPage.constants'
import type {
  Line,
  TableData,
  VariantLineItem,
} from '../models/productComparison'

export type Scores = Record<string, string | undefined>

export type ProductSpecification = {
  field: string
  display: string
  values: Array<{
    label: string
  }>
}

export function getSpecificationField(
  productsSpecifications: ProductSpecification[][],
  field: string,
): Line {
  let title = ''
  const values = productsSpecifications.map((productsSpecification) => {
    const productSpecification = productsSpecification.find(
      (i) => i.field === field,
    )
    if (!productSpecification) return null
    if (productSpecification.display) title = productSpecification.display

    return productSpecification.values.map((i) => i.label).join(', ')
  })

  return {
    type: 'text',
    title,
    values,
  }
}

export function getSpecificationFields(
  productsSpecification: ProductSpecification[][],
  fields: string[],
): Line[] {
  return fields.map((field) =>
    getSpecificationField(productsSpecification, field),
  )
}

export function getScoreField(
  scores: Scores[],
  scoreField: keyof Scores,
): Line {
  return {
    type: 'scores',
    values: scores.map((score) => parseFloat(score?.[scoreField] ?? '0')),
  }
}

function getTextBlockLine(
  textBlocks: ModelComparisonCompareTextBlock[],
  id: string,
): Line | null {
  const textBlock = textBlocks.find((tb) => tb.section === id)
  if (!textBlock?.value) return null

  return { type: 'html', value: textBlock.value }
}

export function getVariantGroupLine(
  variants: Array<Picker[] | null>,
  groupId: string,
  buildUrl: (slug: string, productId: string) => string,
): Extract<Line, { type: 'variants' }> | null {
  let title = ''
  const values = variants.map((productPickers) => {
    const group = productPickers?.find((g) => g.id === groupId)
    if (!group) return null
    if (group.label) title = group.label

    return group.items.map(
      (item): VariantLineItem => ({
        label: item.label,
        url: buildUrl(item.slug ?? '', item.productId),
        price: item.price,
        color: item.color,
      }),
    )
  })

  if (values.every((v) => v === null)) return null

  return { type: 'variants', title, variantGroupId: groupId, values }
}

function getInfoBlockLine(
  textBlocks: ModelComparisonCompareTextBlock[],
  id: string,
  title: string,
): Line | null {
  const textBlock = textBlocks.find((tb) => tb.section === id)
  if (!textBlock?.value) return null

  return { type: 'info-block', title, value: textBlock.value }
}

export type ComparisonTableLabels = {
  introduction: string
  availableModels: string
  conclusion: string
  scores: string
  durability: string
  performance: string
  screenQuality: string
  soundQuality: string
  multimedia: string
  camera: string
  miscelaneous: string
}

export function buildComparisonTable({
  textBlocks,
  specs,
  scores,
  labels,
  buildVariantUrl,
  variants,
}: {
  textBlocks: ModelComparisonCompareTextBlock[]
  specs: ProductSpecification[][]
  scores: Scores[]
  labels: ComparisonTableLabels
  variants?: Array<Picker[] | null>
  buildVariantUrl?: (slug: string, productId: string) => string
}): TableData[] {
  const variantGroupIds = PRODUCT_COMPARISON_VARIANT_GROUP_IDS

  function scoreSection(title: string, scoreId: string, fields: string[]) {
    return {
      title,
      anchor: { id: scoreId, label: title },
      trackingKey: scoreId,
      lines: [
        getScoreField(scores, scoreId),
        getTextBlockLine(textBlocks, scoreId),
        ...getSpecificationFields(specs, fields),
      ].filter(Boolean) as Line[],
    }
  }

  const buildUrl = buildVariantUrl ?? (() => '')

  const table: TableData[] = [
    {
      anchor: { id: 'introduction', label: labels.introduction },
      trackingKey: 'introduction',
      sections: [
        {
          lines: [
            getInfoBlockLine(textBlocks, 'introduction', labels.introduction),
          ].filter(Boolean) as Line[],
        },
      ],
    },
    {
      title: labels.availableModels,
      anchor: { id: 'available-models', label: labels.availableModels },
      trackingKey: 'available_models',
      sections: [
        {
          lines: variantGroupIds
            .map((id) => getVariantGroupLine(variants ?? [], id, buildUrl))
            .filter(Boolean) as Line[],
        },
      ],
    },
    {
      title: labels.scores,
      trackingKey: 'scores',
      sections: [
        scoreSection(
          labels.durability,
          'life_expectancy_score',
          PRODUCT_COMPARISON_DURABILITY_FIELDS,
        ),
        scoreSection(
          labels.performance,
          'performances_score',
          PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
        ),
        scoreSection(
          labels.screenQuality,
          'screen_quality_score',
          PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
        ),
        scoreSection(
          labels.soundQuality,
          'sound_quality_score',
          PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
        ),
        scoreSection(
          labels.multimedia,
          'multimedia_score',
          PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
        ),
        scoreSection(
          labels.camera,
          'camera_score',
          PRODUCT_COMPARISON_CAMERA_FIELDS,
        ),
        scoreSection(
          labels.miscelaneous,
          'miscellaneous',
          PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
        ),
      ],
    },
    {
      title: labels.conclusion,
      separator: true,
      anchor: { id: 'conclusion', label: labels.conclusion },
      trackingKey: 'conclusion',
      sections: [
        {
          lines: [getTextBlockLine(textBlocks, 'conclusion')].filter(
            Boolean,
          ) as Line[],
        },
      ],
    },
  ]

  return cleanTableData(table)
}

export function cleanTableData(table: TableData[]): TableData[] {
  return table
    .map((tableGroup) => {
      const sections = tableGroup.sections
        .map((section) => ({
          ...section,
          lines: section.lines.filter((line) => {
            if (line.type === 'html' || line.type === 'info-block')
              return !!line.value
            if (line.type === 'tags')
              return line.values?.every((v) => v?.length > 0)
            if (line.type === 'scores') return line.values?.every((v) => v > 0)
            if (line.type === 'variants')
              return line.values.some((v) => v !== null)

            return line.values?.every((v) => !!v)
          }),
        }))
        .filter((section) => !!section.lines.length)

      return { ...tableGroup, sections }
    })
    .filter((tableGroup) => !!tableGroup.sections.length)
}
